/**
 * @file     ListingHistoryLive.tsx
 * @purpose  Keeps the listing-history slot mounted across VOW sign-in.
 *           Without access it shows GatedPanel (drawn placeholders, never a
 *           value) and the VOW access modal; unlock refetches /vow-detail in
 *           place, so the real table replaces the gate without waiting on
 *           router.refresh().
 */
'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GatedPanel } from '@/components/gate/GatedPanel';
import { VowAccessModal } from '@/components/vow/VowAccessModal';
import { useVowRegistrationResume } from '@/components/vow/useVowRegistrationResume';
import { useVowUnlockToken } from '@/hooks/useVowUnlock';
import type { ListingSurface } from '@/core/analytics/listingEvents';
import { ListingHistorySection, type HistoryRow, type PriceChange } from './MarketAndHistorySections';

export interface ListingHistoryLiveData {
  rows: HistoryRow[];
  priceChanges: PriceChange[];
  propertyLabel: string;
}

export function ListingHistoryLive({
  listingKey,
  initial,
  historyAccess,
  isAuthenticated,
  needsReactivation,
  headless = false,
  historyOnly = false,
  surface = 'page',
}: {
  listingKey: string;
  initial: ListingHistoryLiveData;
  historyAccess: boolean;
  isAuthenticated: boolean;
  needsReactivation: boolean;
  /** Drop the table heading when a titled ListingSection wraps it. */
  headless?: boolean;
  /** Refetch only the history part of /vow-detail (the quick views). */
  historyOnly?: boolean;
  /** Where the gate shows, for its gate_shown event. */
  surface?: ListingSurface;
}) {
  const { t } = useTranslation();
  const unlockToken = useVowUnlockToken();
  const [data, setData] = useState(initial);
  const [access, setAccess] = useState(historyAccess);
  const [authed, setAuthed] = useState(isAuthenticated);
  const [reactivate, setReactivate] = useState(needsReactivation);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setData(initial);
    setAccess(historyAccess);
    setAuthed(isAuthenticated);
    setReactivate(needsReactivation);
  }, [initial, historyAccess, isAuthenticated, needsReactivation]);

  // A registration interrupted by email confirmation resumes at the phone step.
  useVowRegistrationResume({ authed, locked: !access, onResume: () => setModalOpen(true) });

  useEffect(() => {
    if (unlockToken === 0) return;
    const ctrl = new AbortController();
    fetch(`/api/mls/listings/${encodeURIComponent(listingKey)}/vow-detail${historyOnly ? '?part=history' : ''}`, {
      signal: ctrl.signal,
      cache: 'no-store',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((payload: {
        listingHistory?: ListingHistoryLiveData;
        showVow?: boolean;
        isAuthenticated?: boolean;
        needsReactivation?: boolean;
      } | null) => {
        if (ctrl.signal.aborted || !payload?.listingHistory) return;
        setData({
          rows: payload.listingHistory.rows ?? [],
          priceChanges: payload.listingHistory.priceChanges ?? [],
          propertyLabel: payload.listingHistory.propertyLabel || initial.propertyLabel,
        });
        setAccess(!!payload.showVow);
        setAuthed(!!payload.isAuthenticated);
        setReactivate(!!payload.needsReactivation);
      })
      .catch((err: unknown) => {
        if ((err as { name?: string } | undefined)?.name === 'AbortError') return;
      });
    return () => ctrl.abort();
  }, [listingKey, unlockToken, initial.propertyLabel, historyOnly]);

  if (access) {
    return (
      <ListingHistorySection
        propertyLabel={data.propertyLabel}
        rows={data.rows}
        priceChanges={data.priceChanges}
        isAuthenticated
        variant="expanded"
        headless={headless}
      />
    );
  }

  return (
    <>
      {reactivate ? (
        <GatedPanel
          title={t('listingDetail.history.pausedTitle', { defaultValue: 'Your access is paused' })}
          description={t('listingDetail.history.pausedBody', { defaultValue: 'Turn it back on to see price changes and nearby sales.' })}
          ctaLabel={t('vowGate.reactivate', { defaultValue: 'Turn access back on' })}
          onUnlock={() => setModalOpen(true)}
          trackAs={{ surface, listingKey, field: 'listingHistory' }}
        />
      ) : (
        <GatedPanel
          title={t('listingDetail.history.gateTitle', { defaultValue: 'Price history and sold data need a free account' })}
          description={t('listingDetail.history.gateBody', { defaultValue: 'See price changes and nearby sales with a free account.' })}
          ctaLabel={
            authed
              ? t('listingDetail.history.gateCtaAuthed', { defaultValue: 'Turn on access' })
              : t('listingDetail.history.gateCta', { defaultValue: 'Create account' })
          }
          onUnlock={() => setModalOpen(true)}
          trackAs={{ surface, listingKey, field: 'listingHistory' }}
        />
      )}
      <VowAccessModal open={modalOpen} onClose={() => setModalOpen(false)} authed={authed} needsReactivation={reactivate} />
    </>
  );
}
